import type { SxProps, Theme } from "@mui/material/styles";
import { useFormContext } from "react-hook-form";

import { Box } from "ui/box";
import { Checkbox } from "ui/checkbox";
import { Chip } from "ui/chip";
import { Select, type OptionType } from "ui/select";

import { useCreativeTemplateOptions } from "hooks/api/use-creative-template-options";

import type {
  AddCreativesWithWriterFormData,
  WriterCreative,
} from "components/modals/add-creatives-with-writer-modal/add-creatives-with-writer-modal.types";

import { toSpacedTitleCase } from "utilities";
import { getTouchpointClassChipColor } from "utilities/touchpoint-class-chip";

import Icons from "assets";

const POPULATION_LABELS: Record<WriterCreative["populationType"], string> = {
  STUDENT: "Student",
  PARENT: "Parent",
};

export const TEMPLATE_COLUMN_SX: SxProps<Theme> = { flex: 1, minWidth: 0 };

const getChannelIcon = (channel: WriterCreative["touchpointChannel"]) => {
  if (channel === "email") {
    return <Icons.Email className="size-4 shrink-0 text-gray-600" aria-hidden />;
  }

  if (channel === "web-page") {
    return <Icons.LandingPage className="size-4 shrink-0 text-gray-600" aria-hidden />;
  }

  return <Icons.Other className="size-4 shrink-0 text-gray-600" aria-hidden />;
};

interface CreativeChecklistItemProps {
  creative: WriterCreative;
}

const CreativeChecklistItem = ({ creative }: CreativeChecklistItemProps) => {
  const { watch, setValue } = useFormContext<AddCreativesWithWriterFormData>();
  const selectedCreativeIds = watch("selectedCreativeIds") ?? [];
  const creativeTemplates = watch("creativeTemplates") ?? {};
  const isSelected = selectedCreativeIds.includes(creative.id);
  const { templateOptions, isLoading: isLoadingTemplates } = useCreativeTemplateOptions(
    creative.touchpointChannel,
  );

  const handleChange = (checked: boolean) => {
    const nextSelectedCreativeIds = checked
      ? [...selectedCreativeIds, creative.id]
      : selectedCreativeIds.filter((id) => id !== creative.id);

    setValue("selectedCreativeIds", nextSelectedCreativeIds);
  };

  const handleTemplateChange = (selected: OptionType | null) => {
    setValue("creativeTemplates", {
      ...creativeTemplates,
      [creative.id]: (selected?.value as string) ?? "",
    });
  };

  const selectedTemplateId = creativeTemplates[creative.id];

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1, pr: 1.5 }}>
      <div className="flex flex-1 items-center gap-2 min-w-0">
        <Checkbox
          size="sm"
          checked={isSelected}
          onChange={handleChange}
          name={`creative-${creative.id}`}
          label={
            <span className="flex items-center gap-2 text-gray-900">
              <span className="flex items-center gap-2">
                {getChannelIcon(creative.touchpointChannel)}
                {creative.name}
                <span className="text-gray-500">{POPULATION_LABELS[creative.populationType]}</span>
              </span>
              {creative.classes.length > 0 && (
                <span className="flex items-center gap-2">
                  {creative.classes.map((classType) => (
                    <Chip
                      key={`${creative.id}-${classType}`}
                      size="small"
                      variant="outlined"
                      color={getTouchpointClassChipColor(classType)}
                      label={toSpacedTitleCase(classType)}
                    />
                  ))}
                </span>
              )}
            </span>
          }
        />
      </div>
      <Box sx={TEMPLATE_COLUMN_SX}>
        <Select
          selectName={`creative-template-${creative.id}`}
          ariaLabel={`Select creative template for ${creative.name}`}
          size="small"
          disabled={!isSelected || isLoadingTemplates}
          loading={isLoadingTemplates}
          options={templateOptions}
          value={templateOptions.find((option) => option.value === selectedTemplateId) ?? null}
          onChange={(selected) => handleTemplateChange(selected)}
          placeholder="Select template"
          startIcon={<Icons.Search className="text-aqua-600" />}
        />
      </Box>
    </Box>
  );
};

export default CreativeChecklistItem;
